<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div>
              <div class="font-weight-bold text-body-2 lowercase">
                {{ userDetails?.name || 'Carregando...' }}
              </div>
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="bg-grey-lighten-5 main-content" style="margin-top: 20px">
      <v-container fluid class="pa-6">
        <!-- Page Header with Animated Title -->
        <div class="d-flex justify-space-between align-center mb-8">
          <div class="d-flex align-center" style="gap: 16px">
          </div>
        </div>

        <!-- Animated Title Section -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card 
              class="pa-6 animated-title-card" 
              elevation="8" 
              rounded="xl"
              :color="colors.primaryDark"
            >
              <div class="text-center">
                <div class="animated-title-container">
                  <h1 class="animated-title">
                    <span class="title-word">Painel</span>
                    <span class="title-word">de</span>
                    <span class="title-word">Controle</span>
                    <span class="title-word">da</span>
                    <span class="title-word title-highlight">IA</span>
                    <span class="title-word title-highlight">SICON</span>
                  </h1>
                  <div class="title-underline"></div>
                </div>
                <p class="text-subtitle-1 mt-4 text-white opacity-90">
                  Sistema Inteligente de Controle e Monitoramento
                </p>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Informações do Condomínio Selecionado -->
        <v-row v-if="isMounted && condominioDetalhes" class="mb-6">
          <v-col cols="12">
            <v-alert
              :color="colors.primaryLight"
              variant="tonal"
              icon="fas fa-building"
              class="mb-4 enhanced-alert"
            >
              <v-alert-title class="text-h6 font-weight-bold">
                {{ condominioDetalhes.nome || 'Nome não disponível' }}
              </v-alert-title>
              <div class="mt-2">
                <div><strong>ID:</strong> {{ condominioDetalhes.id || 'N/A' }}</div>
                <div v-if="condominioDetalhes.endereco"><strong>Endereço:</strong> {{ condominioDetalhes.endereco }}</div>
                <div v-if="condominioDetalhes.telefone"><strong>Telefone:</strong> {{ condominioDetalhes.telefone }}</div>
                <div v-if="condominioDetalhes.email"><strong>Email:</strong> {{ condominioDetalhes.email }}</div>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Stats Cards -->
        <v-row class="mb-8">
          <v-col
            v-for="(stat, index) in statsCards"
            :key="stat.title"
            cols="12"
            sm="6"
            lg="3"
          >
            <v-card 
              class="pa-6 stat-card enhanced-stat-card" 
              elevation="6" 
              rounded="xl"
              :style="{ 'animation-delay': `${index * 0.15}s` }"
            >
              <div class="d-flex align-center">
                <v-avatar size="68" :color="stat.iconBg" class="mr-4 icon-avatar enhanced-icon">
                  <v-icon size="34" :color="stat.iconColor" class="stat-icon">
                    {{ stat.icon }}
                  </v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="text-caption text-grey-darken-2 mb-1 font-weight-medium">
                    {{ stat.title }}
                  </div>
                  <div class="text-h4 font-weight-bold stat-value enhanced-stat-value">
                    {{ stat.value }}
                  </div>
                  <div class="d-flex align-center mt-2">
                    <v-icon size="14" color="success" class="mr-1 bounce-icon">fas fa-arrow-up</v-icon>
                    <span class="text-caption text-success font-weight-medium">+12% vs mês anterior</span>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- User Profile and Permissions Row -->
        <v-row class="mb-8">
          <!-- User Profile Section -->
          <v-col cols="12" lg="8">
            <v-card class="pa-0 enhanced-main-card" elevation="8" rounded="xl" height="580">
              <div class="pa-6 border-b main-card-header">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <h2 class="text-h5 font-weight-bold">Informações do Usuário</h2>
                    <p class="text-subtitle-2 text-grey-darken-1 mb-0">Dados pessoais e configurações do perfil</p>
                  </div>
                  <v-chip 
                    :color="userDetails.isActive ? 'success' : 'error'" 
                    variant="flat" 
                    class="floating-chip"
                    :prepend-icon="userDetails.isActive ? 'fas fa-check-circle' : 'fas fa-times-circle'"
                  >
                    {{ userDetails.isActive ? 'Ativo' : 'Inativo' }}
                  </v-chip>
                </div>
              </div>

              <div class="pa-6" style="height: calc(100% - 110px); overflow:auto;">
                <div class="d-flex align-start" style="gap: 32px;">
                  <div class="text-center">
                  </div>

                  <div class="flex-grow-1">
                    <div class="d-flex justify-space-between align-center mb-4">
                      <div>
                        <h3 class="text-h5 font-weight-bold mb-1">{{ userDetails.name || 'Carregando...' }}</h3>
                        <p class="text-body-1 mb-2 text-grey-darken-1">{{ userDetails.email || 'Carregando...' }}</p>
                        <div class="d-flex align-center" style="gap: 8px;">
                          <v-icon size="16" :color="colors.primaryLight">fas fa-building</v-icon>
                          <span class="text-body-2 text-grey-darken-1">{{ userDetails.department?.name || 'N/A' }}</span>
                        </div>
                      </div>

                      <div class="d-flex" style="gap: 12px">
                        <v-btn 
                          variant="outlined" 
                          @click="copyToClipboard(userDetails.email)" 
                          prepend-icon="fas fa-copy"
                          class="enhanced-btn"
                          size="small"
                        >
                          Copiar Email
                        </v-btn>
                      </div>
                    </div>

                    <v-divider class="mb-6"></v-divider>

                    <div class="user-details-grid enhanced-grid">
                      <div class="detail-row enhanced-detail">
                        <div class="detail-label">📱 Telefone</div>
                        <div class="detail-value">{{ userDetails.phone || 'Não informado' }}</div>
                      </div>

                      <div class="detail-row enhanced-detail">
                        <div class="detail-label">🆔 CPF</div>
                        <div class="detail-value">{{ userDetails.cpf || 'Não informado' }}</div>
                      </div>

                      <div class="detail-row enhanced-detail">
                        <div class="detail-label">🏢 Departamento</div>
                        <div class="detail-value">{{ userDetails.department?.name || 'N/A' }}</div>
                      </div>

                      <div class="detail-row enhanced-detail">
                        <div class="detail-label">👤 ID do Usuário</div>
                        <div class="detail-value">#{{ userDetails.id || 'N/A' }}</div>
                      </div>

                      <div class="detail-row enhanced-detail">
                        <div class="detail-label">📅 Criado em</div>
                        <div class="detail-value">{{ formattedCreatedAt }}</div>
                      </div>

                      <div class="detail-row enhanced-detail">
                        <div class="detail-label">⚡ Status do Sistema</div>
                        <div class="detail-value">
                          <v-chip 
                            :color="userDetails.status ? 'success' : 'warning'" 
                            size="small" 
                            class="status-indicator"
                          >
                            {{ userDetails.status ? 'Conectado' : 'Desconectado' }}
                          </v-chip>
                        </div>
                      </div>
                    </div>

                    <div class="mt-6">
                      <v-chip 
                        :color="colors.primaryLight" 
                        variant="tonal" 
                        class="mr-2 mb-2 enhanced-chip"
                        prepend-icon="fas fa-building"
                      >
                        Depto: {{ userDetails.department?.name }}
                      </v-chip>
                      <v-chip 
                        :color="colors.primaryGreen" 
                        variant="tonal" 
                        class="mb-2 enhanced-chip"
                        prepend-icon="fas fa-calendar-alt"
                      >
                        Desde: {{ formattedYear }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Allowed Screens Section -->
          <v-col cols="12" lg="4">
            <v-card class="pa-0 enhanced-side-card" elevation="8" rounded="xl" height="580">
              <div class="pa-6 border-b side-card-header">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <h2 class="text-h5 font-weight-bold">Telas Permitidas</h2>
                    <p class="text-subtitle-2 text-grey-darken-1 mb-0">Módulos e funcionalidades disponíveis</p>
                  </div>
                  <v-chip 
                    :color="colors.primaryGreen" 
                    variant="flat" 
                    size="small"
                    class="floating-chip"
                    prepend-icon="fas fa-shield-alt"
                  >
                    {{ allowedScreensCount }} Módulos
                  </v-chip>
                </div>
              </div>

              <div class="pa-4" style="height: calc(100% - 110px); overflow-y: auto;">
                <div v-if="!userDetails.allowedScreens || userDetails.allowedScreens.length === 0" class="text-center py-8">
                  <v-icon size="60" color="grey-lighten-2" class="mb-4">fas fa-lock</v-icon>
                  <p class="text-grey-darken-1">Nenhuma tela permitida</p>
                </div>

                <div v-else class="permissions-grid">
                  <v-card
                    v-for="(screen, index) in allowedScreensDetails"
                    :key="screen.value"
                    class="pa-4 mb-3 permission-card enhanced-permission-card"
                    :style="{ 'animation-delay': `${index * 0.1}s` }"
                    elevation="2"
                    rounded="lg"
                  >
                    <div class="d-flex align-center">
                      <v-avatar 
                        size="45" 
                        :color="getScreenColor(screen.value)" 
                        class="mr-3 permission-icon"
                      >
                        <v-icon :color="getScreenIconColor(screen.value)" size="20">
                          {{ screen.icon }}
                        </v-icon>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div class="font-weight-bold text-body-1 mb-1">
                          {{ screen.title }}
                        </div>
                        <div class="text-caption text-grey-darken-1">
                          {{ getScreenDescription(screen.value) }}
                        </div>
                      </div>
                      <v-icon 
                        size="16" 
                        color="success" 
                        class="permission-check"
                      >
                        fas fa-check-circle
                      </v-icon>
                    </div>
                  </v-card>
                </div>

                <!-- Restricted Screens Preview -->
                <div v-if="restrictedScreens.length > 0" class="mt-6">
                  <v-divider class="mb-4"></v-divider>
                  <div class="text-body-2 font-weight-medium text-grey-darken-2 mb-3">
                    <v-icon size="16" class="mr-1">fas fa-eye-slash</v-icon>
                    Telas Restritas ({{ restrictedScreens.length }})
                  </div>
                  
                  <div class="restricted-screens">
                    <v-card
                      v-for="screen in restrictedScreens.slice(0, 3)"
                      :key="screen.value"
                      class="pa-3 mb-2 restricted-card"
                      elevation="1"
                      rounded="md"
                    >
                      <div class="d-flex align-center opacity-60">
                        <v-avatar size="35" color="grey-lighten-3" class="mr-3">
                          <v-icon color="grey-darken-1" size="16">{{ screen.icon }}</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="text-body-2 text-grey-darken-1">{{ screen.title }}</div>
                        </div>
                        <v-icon size="14" color="error">fas fa-times-circle</v-icon>
                      </div>
                    </v-card>
                    
                    <div v-if="restrictedScreens.length > 3" class="text-center mt-2">
                      <span class="text-caption text-grey-darken-2">
                        +{{ restrictedScreens.length - 3 }} mais restritas
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, onBeforeUnmount } from "vue";
import { useRouter } from 'vue-router';
import axios from "axios";
import { useAuthStore } from '@/stores/auth'
import { menuItems } from '@/constants/MenuItems'

const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');

// Available screens configuration
const availableScreens = [
  {
    title: "Reportes da IA",
    value: "ticket",
    icon: "fa-solid fa-robot"
  },
  {
    title: "Arquivos",
    value: "files",
    icon: "fa-solid fa-file"
  },
  {
    title: "Configurações da IA",
    value: "config",
    icon: "fa-solid fa-gear"
  },
  {
    title: "Transferências",
    value: "transfers",
    icon: "fas fa-exchange-alt"
  },
  {
    title: "Financeiro",
    value: "financer",
    icon: "fas fa-file-invoice-dollar"
  },
  {
    title: "Usuários",
    value: "residents",
    icon: "fas fa-users"
  },
  {
    title: "Tarefas",
    value: "calendar",
    icon: "fas fa-tasks"
  },
  {
    title: "Chat",
    value: "settings",
    icon: "fa-solid fa-comment"
  },
  {
    title: "Conexões WhatsApp",
    value: "config-wpp",
    icon: "fa-brands fa-square-whatsapp"
  },
  {
    title: "Sicon GPT",
    value: "sicon-gpt",
    icon: "fa-solid fa-comments"
  }
];

// Dados mockados para exibição imediata
const userDetails = ref({
  name: 'Carregando...',
  email: 'Carregando...',
  allowedScreens: []
})

const authStore = useAuthStore()
const auth = useAuthStore()
const token = auth?.token || null
const drawer = ref(true);
const router = useRouter();
const isMounted = ref(false);
const condominioDetalhes = ref(null);

const fetchUserDetails = async () => {
  if (!isMounted.value || !token) return;

  try {
    const response = await axios.get(`${API_BASE}/me`, { 
      headers: { Authorization: `Bearer ${token}` } 
    });

    if (isMounted.value && response.data) {
      userDetails.value = response.data;
    }
  } catch (error) {
    if (isMounted.value) {
      console.error('Erro fetch /me, usando dados locais')
    }
  }
}

const logout = () => {
  if (authStore && typeof authStore.logout === 'function') {
    authStore.logout();
  }
  if (router && typeof router.push === 'function') {
    router.push('/');
  }
}

const copyToClipboard = (text) => {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(() => {
    // toast opcional
  }).catch(() => {});
}

const allowedScreensDetails = computed(() => {
  if (!userDetails.value.allowedScreens) return [];
  
  return userDetails.value.allowedScreens.map(screenValue => {
    return availableScreens.find(screen => screen.value === screenValue) || {
      title: screenValue,
      value: screenValue,
      icon: 'fas fa-question'
    };
  }).filter(Boolean);
});

const restrictedScreens = computed(() => {
  if (!userDetails.value.allowedScreens) return availableScreens;
  
  return availableScreens.filter(screen => 
    !userDetails.value.allowedScreens.includes(screen.value)
  );
});

const allowedScreensCount = computed(() => {
  return userDetails.value.allowedScreens?.length || 0;
});

const formattedCreatedAt = computed(() => {
  try {
    return new Date(userDetails.value.createdAt).toLocaleString('pt-BR');
  } catch (e) { 
    return 'N/A' 
  }
})

const formattedYear = computed(() => {
  try {
    return new Date(userDetails.value.createdAt).getFullYear();
  } catch (e) { 
    return 'N/A' 
  }
})

// Helper functions for screen styling
const getScreenColor = (screenValue) => {
  const colorMap = {
    'ticket': '#57C2E3',
    'files': '#A3C940',
    'config': '#FF6B6B',
    'transfers': '#4ECDC4',
    'residents': '#45B7D1',
    'financial': '#96CEB4',
    'calendar': '#FFEAA7',
    'settings': '#DDA0DD',
    'config-wpp': '#25D366',
    'sicon-gpt': '#FF7675'
  };
  return colorMap[screenValue] || '#123A5D';
};

const getScreenIconColor = (screenValue) => {
  const whiteIcons = ['ticket', 'files', 'config', 'transfers', 'residents', 'financial', 'settings', 'config-wpp', 'sicon-gpt'];
  return whiteIcons.includes(screenValue) ? 'white' : '#123A5D';
};

const getScreenDescription = (screenValue) => {
  const descriptions = {
    'ticket': 'Análises e relatórios automatizados',
    'files': 'Gerenciamento de documentos',
    'config': 'Configurações do sistema IA',
    'transfers': 'Movimentações financeiras',
    'residents': 'Gestão de usuários do sistema',
    'calendar': 'Agenda e tarefas organizadas',
    'settings': 'Comunicação em tempo real',
    'config-wpp': 'Integração WhatsApp Business',
    'sicon-gpt': 'Assistente inteligente avançado'
  };
  return descriptions[screenValue] || 'Funcionalidade do sistema';
};

onMounted(async () => {
  isMounted.value = true
  await nextTick()
  await fetchUserDetails()
})

onBeforeUnmount(() => { 
  isMounted.value = false 
})

const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};
</script>

<style scoped>
/* Animated Title Styles */
.animated-title-card {
  background: linear-gradient(135deg, #123A5D 0%, #1e4a6f 50%, #2a5980 100%);
  position: relative;
  overflow: hidden;
}

.animated-title-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shimmer 3s infinite;
}

.animated-title-container {
  position: relative;
  z-index: 2;
}

.animated-title {
  font-size: 3.5rem;
  font-weight: 900;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0;
  line-height: 1.1;
}

.title-word {
  display: inline-block;
  margin-right: 0.5rem;
  animation: fadeInScale 0.8s ease-out forwards;
  opacity: 0;
}

.title-word:nth-child(1) { animation-delay: 0.1s; }
.title-word:nth-child(2) { animation-delay: 0.3s; }
.title-word:nth-child(3) { animation-delay: 0.5s; }
.title-word:nth-child(4) { animation-delay: 0.7s; }
.title-word:nth-child(5) { animation-delay: 0.9s; }
.title-word:nth-child(6) { animation-delay: 1.1s; }

.title-highlight {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: fadeInScale 0.8s ease-out forwards, glow 2s infinite alternate;
}

.title-underline {
  height: 4px;
  background: linear-gradient(90deg, #57C2E3, #A3C940, #57C2E3);
  background-size: 200% 100%;
  animation: gradientMove 2s infinite, expandWidth 1.5s ease-out forwards;
  margin-top: 1rem;
  border-radius: 2px;
  width: 0%;
}

/* Enhanced Card Styles */
.enhanced-stat-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 2px solid rgba(87, 194, 227, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.enhanced-stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(87, 194, 227, 0.1), transparent);
  transition: left 0.5s ease;
}

.enhanced-stat-card:hover::before {
  left: 100%;
}

.enhanced-stat-card:hover {
  transform: translateY(-12px) scale(1.03);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border-color: rgba(87, 194, 227, 0.3);
}

.enhanced-icon {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.enhanced-stat-card:hover .enhanced-icon {
  transform: scale(1.15) rotate(10deg);
}

.enhanced-stat-value {
  background: linear-gradient(45deg, #123A5D, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% 100%;
  animation: gradientMove 3s infinite;
}

.bounce-icon {
  animation: bounce 2s infinite;
}

/* Enhanced Main Card */
.enhanced-main-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 2px solid rgba(87, 194, 227, 0.1);
  position: relative;
  overflow: hidden;
}

.main-card-header {
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  border-bottom: 2px solid rgba(87, 194, 227, 0.1) !important;
}

.floating-chip {
  animation: float 3s ease-in-out infinite;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.enhanced-avatar {
  border: 4px solid rgba(87, 194, 227, 0.3);
  box-shadow: 0 8px 20px rgba(87, 194, 227, 0.2);
  transition: all 0.3s ease;
}

.enhanced-avatar:hover {
  transform: scale(1.05);
  border-color: rgba(87, 194, 227, 0.5);
}

.enhanced-btn {
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.enhanced-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.enhanced-grid {
  background: linear-gradient(135deg, rgba(87, 194, 227, 0.05) 0%, rgba(163, 201, 64, 0.05) 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(87, 194, 227, 0.1);
}

.enhanced-detail {
  background: rgba(255, 255, 255, 0.8);
  padding: 16px;
  border-radius: 12px;
  border-left: 4px solid #57C2E3;
  transition: all 0.3s ease;
}

.enhanced-detail:hover {
  transform: translateX(4px);
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.enhanced-chip {
  transition: all 0.3s ease;
}

.enhanced-chip:hover {
  transform: scale(1.05);
}

.status-indicator {
  animation: pulse 2s infinite;
}

/* Enhanced Side Card */
.enhanced-side-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 2px solid rgba(87, 194, 227, 0.1);
  position: relative;
}

.side-card-header {
  background: linear-gradient(135deg, rgba(87, 194, 227, 0.05) 0%, rgba(163, 201, 64, 0.05) 100%);
  border-bottom: 2px solid rgba(87, 194, 227, 0.1) !important;
}

/* Permission Cards Styles */
.permissions-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.enhanced-permission-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 2px solid rgba(87, 194, 227, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: slideInLeft 0.6s ease-out forwards;
  opacity: 0;
}

.enhanced-permission-card:hover {
  transform: translateX(8px) translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: rgba(87, 194, 227, 0.3);
}

.permission-icon {
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.enhanced-permission-card:hover .permission-icon {
  transform: scale(1.1) rotate(5deg);
}

.permission-check {
  transition: all 0.3s ease;
}

.enhanced-permission-card:hover .permission-check {
  transform: scale(1.2);
  color: #4CAF50 !important;
}

.restricted-card {
  background: linear-gradient(135deg, #f9f9f9 0%, #f5f5f5 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.restricted-card:hover {
  background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
  transform: translateX(4px);
}

.enhanced-alert {
  border-left: 6px solid #57C2E3;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.15);
}

/* Base Styles (keeping existing ones) */
.user-info-details {
  background: rgba(87, 194, 227, 0.05);
  border-radius: 12px;
  padding: 16px;
}

.user-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
}

.detail-row {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 4px;
}

.detail-value {
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
}

.pulse {
  animation: pulse 2s infinite;
}

.lowercase {
  text-transform: lowercase;
}

/* Enhanced Animations */
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes glow {
  0%, 100% {
    text-shadow: 0 0 5px rgba(87, 194, 227, 0.5), 0 0 10px rgba(87, 194, 227, 0.3);
  }
  50% {
    text-shadow: 0 0 10px rgba(87, 194, 227, 0.8), 0 0 20px rgba(87, 194, 227, 0.5), 0 0 30px rgba(163, 201, 64, 0.3);
  }
}

@keyframes expandWidth {
  from { width: 0%; }
  to { width: 60%; margin: 1rem auto 0; }
}

@keyframes gradientMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
  60% { transform: translateY(-2px); }
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(76, 175, 80, 0.8);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Component Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.menu-item .v-list-item__content {
  display: flex;
  align-items: center;
}

.menu-item .item-title {
  white-space: nowrap;
  text-align: left;
}

.left-panel, .left-panel * {
  text-align: left !important;
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0px 0;
  position: relative;
  overflow: hidden;
  padding-left: 8px;
  margin-top: 4px;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.status-chip {
  animation: slideIn 0.5s ease-out;
}

.stat-card {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%);
  border: 1px solid rgba(87, 194, 227, 0.1);
}

.stat-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.icon-avatar {
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-card:hover .icon-avatar {
  transform: scale(1.1) rotate(5deg);
}

.stat-icon {
  transition: all 0.3s ease;
}

.stat-value {
  background: linear-gradient(45deg, #123A5D, #57C2E3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .animated-title {
    font-size: 2.8rem;
  }
  
  .user-details-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 960px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .user-profile span:last-child {
    display: none;
  }
  
  .animated-title {
    font-size: 2.2rem;
  }
}

@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }
  
  .animated-title {
    font-size: 1.8rem;
  }
  
  .title-word {
    display: block;
    margin-bottom: 0.2rem;
  }
  
  .enhanced-grid {
    padding: 16px;
  }
  
  .user-details-grid {
    gap: 8px;
  }
}
</style>
